import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Container,
  alpha
} from '@mui/material';
import { COLORS } from '../../theme/themeConstants';

interface SettlementHyperPayWizardProps {
  checkoutId?: string;
  settlementPaymentId?: string;
}

const SettlementHyperPayWizard = ({ 
  checkoutId: propCheckoutId, 
  settlementPaymentId: propSettlementPaymentId 
}: SettlementHyperPayWizardProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formContainerRef = useRef<HTMLDivElement>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Get checkoutId from props or URL params
  const checkoutId = propCheckoutId || searchParams.get('checkoutId') || searchParams.get('id') || '';
  const settlementPaymentId = propSettlementPaymentId || searchParams.get('settlementPaymentId') || '';
  const merchantId = searchParams.get('merchantId') || '';

  // HyperPay configuration - update these based on your environment
  const HYPERPAY_BASE_URL = import.meta.env.VITE_HYPERPAY_BASE_URL || 'https://eu-test.oppwa.com';
  
  useEffect(() => {
    if (!checkoutId) {
      setError('No checkout ID provided');
      setLoading(false);
      return;
    }

    // Configure HyperPay widget options before loading the script
    (window as any).wpwlOptions = {
      style: 'plain',
      locale: 'en',
      onReady: function() {
        console.log('HyperPay widget is ready');
      }
    };

    // Load HyperPay script
    const script = document.createElement('script');
    script.src = `${HYPERPAY_BASE_URL}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;
    
    script.onload = () => {
      console.log('HyperPay script loaded successfully');
      setScriptLoaded(true);
      setLoading(false);
    };
    
    script.onerror = () => {
      console.error('Failed to load HyperPay script');
      setError('Failed to load payment form. Please try again.');
      setLoading(false);
    };

    document.body.appendChild(script);

    // Cleanup
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Clean up widget options
      delete (window as any).wpwlOptions;
    };
  }, [checkoutId, HYPERPAY_BASE_URL, settlementPaymentId, merchantId]);

  const handleCancel = () => {
    navigate('/layout/settlements');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)'
    }}>
      <Container maxWidth="md">
        <Paper sx={{
          p: 4,
          border: '1px solid #e8eaed',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
        }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 1
            }}>
              Complete Your Settlement Payment
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Secure payment powered by HyperPay
            </Typography>
            {settlementPaymentId && (
              <Typography variant="caption" sx={{
                display: 'block',
                mt: 1,
                color: 'text.secondary'
              }}>
                Settlement Payment: {settlementPaymentId}
              </Typography>
            )}
          </Box>

          {/* Loading State */}
          {loading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 6
            }}>
              <CircularProgress size={40} sx={{ color: COLORS.primary.main }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading payment form...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && !loading && (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="outlined"
                onClick={handleCancel}
                fullWidth
                sx={{ mt: 2 }}
              >
                Go Back to Settlements
              </Button>
            </Box>
          )}

          {/* HyperPay Payment Form */}
          {scriptLoaded && !error && (
            <Box>
              <Box
                ref={formContainerRef}
                sx={{
                  '& form': {
                    '& .wpwl-form': {
                      fontFamily: 'inherit'
                    },
                    '& .wpwl-button': {
                      background: COLORS.primary.main,
                      borderColor: COLORS.primary.main,
                      '&:hover': {
                        background: alpha(COLORS.primary.main, 0.9)
                      }
                    },
                    '& .wpwl-control': {
                      borderRadius: '8px'
                    }
                  }
                }}
              >
                {/* HyperPay will inject the form here */}
                {/* The shopperResultUrl must be set by backend during checkout creation */}
                {/* Do NOT add an action attribute here as it will conflict with backend configuration */}
                <form
                  className="paymentWidgets"
                  data-brands="VISA MASTER MADA"
                />
              </Box>

              {/* Info Message */}
              <Box sx={{
                mt: 3,
                p: 2,
                background: alpha(COLORS.primary.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(COLORS.primary.main, 0.2)}`
              }}>
                <Typography variant="body2" sx={{
                  color: 'text.secondary',
                  textAlign: 'center'
                }}>
                  🔒 Your payment information is secure and encrypted
                </Typography>
              </Box>

              {/* Cancel Button */}
              <Button
                variant="text"
                onClick={handleCancel}
                fullWidth
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  '&:hover': {
                    background: alpha(COLORS.primary.main, 0.05)
                  }
                }}
              >
                Cancel and Go Back to Settlements
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default SettlementHyperPayWizard;

